import { ChevronRight } from '@/src/assets/icons/ChevronRight'
import { mesaColorNames, typography } from '@/src/common/theme'
import { type ComponentProps, type PropsWithChildren, useId } from 'react'
import { StyleSheet, Text } from 'react-native'
import { Separator, Accordion as TamaguiAccordion, View } from 'tamagui'

export function Accordion({
  trigger,
  children,
  index = 0,
  triggerProps,
  contentProps,
  ...props
}: (
  | { trigger: false; open?: boolean }
  | {
      trigger:
        | string
        | PropsWithChildren['children']
        | ((props: { open: boolean }) => PropsWithChildren['children'])
    }
) & {
  index?: number
  triggerProps?: ComponentProps<typeof TamaguiAccordion.Trigger>
  contentProps?: ComponentProps<typeof TamaguiAccordion.Content>
} & React.PropsWithChildren) {
  const id = useId()

  return (
    <>
      {index ? (
        <Separator
          style={{
            borderColor: mesaColorNames['global/grey/20 #E6E9EB'],
            marginTop: 16,
            marginBottom: 16,
          }}
        />
      ) : null}
      <TamaguiAccordion
        overflow="hidden"
        type="multiple"
        value={'open' in props && props.open ? [id] : undefined}
      >
        <TamaguiAccordion.Item value={id}>
          {trigger ? (
            <TamaguiAccordion.Trigger
              borderWidth={0}
              pressStyle={{
                // @ts-expect-error tamagui types are being weird again
                backgroundColor: 'transparent',
                // emulates TouchableOpacity's default https://reactnative.dev/docs/touchableopacity#activeopacity
                opacity: 0.2,
              }}
              // needed for web
              // @ts-expect-error tamagui types are being weird again
              focusStyle={{ backgroundColor: 'transparent' }}
              {...triggerProps}
              style={StyleSheet.flatten([
                {
                  paddingTop: 0,
                  paddingBottom: 0,
                  paddingLeft: 0,
                  paddingRight: 0,
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 8,
                },
                StyleSheet.flatten(triggerProps?.style),
              ])}
            >
              {(props: { open: boolean }) => (
                <>
                  {typeof trigger === 'string' ? (
                    <Text style={[typography['heading/heading5'], { flex: 1 }]}>{trigger}</Text>
                  ) : typeof trigger === 'function' ? (
                    trigger(props)
                  ) : (
                    trigger
                  )}
                  <View
                    style={{ marginLeft: 'auto' }}
                    animation="quick"
                    rotate={props.open ? '-90deg' : '90deg'}
                  >
                    <ChevronRight color={mesaColorNames['Greyscale/500 #6E7375']} />
                  </View>
                </>
              )}
            </TamaguiAccordion.Trigger>
          ) : null}
          <TamaguiAccordion.HeightAnimator animation="quick">
            <TamaguiAccordion.Content
              animation="quick"
              exitStyle={{ opacity: 0 }}
              {...contentProps}
              style={StyleSheet.flatten([
                { paddingTop: 0, paddingLeft: 0, paddingRight: 0, paddingBottom: 0 },
                StyleSheet.flatten(contentProps?.style),
              ])}
            >
              {children}
            </TamaguiAccordion.Content>
          </TamaguiAccordion.HeightAnimator>
        </TamaguiAccordion.Item>
      </TamaguiAccordion>
    </>
  )
}
